import { createSelector } from '@reduxjs/toolkit';

import { PurchasedPoiRowDataType } from 'features/my-zone/purchased-pois/shared/types/types';
import {
    normalizeCustomPoiData,
    normalizePlacesForTagsData,
    normalizePoiData,
    normalizeVenueSuggestion,
} from './normalizers';
import { AppState } from 'store/store';
import { CreatedPoisRowDataType } from 'features/my-zone/created-pois/view-pois/types/types';

import { MyZoneState } from './slice';
import { TagsRowDataType } from 'features/my-zone/tags/tags-page/types/types';
import { sumBy } from 'lodash';
import { getEntityTypesByReportType } from 'core/entities';
import { VenueSuggestion } from 'features/my-zone/tags/create-new-tag/types/types';
import type { PropertiesGroup } from 'types/properties-data';
import { userCustomSettingsRecentPoiIdsSelector } from 'store/selectors/app-selectors';
import {
    getRecentEntitiesByReportType,
    sortByTimestamp,
} from 'core/services/recent-entities-service/recent-entities-service';
import { RecentEntity } from 'core/entities/recent-entity/recent-entity';
import { AllEntitiesSelectorType } from 'features/my-zone/shared/types/types';
import { ComparisonDates } from 'types/properties-data';
import type { PlacerEntityWrapper, PlaceType } from '@placer-ui/types';
import { Complex } from '@placer-ui/types/src/entities/place';

export const selectMyZoneState = (state: AppState) => state.myZone as MyZoneState;

export const useMyZoneSelectors = () => {
    const selectIsLoadingContent = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isLoadingContent,
    );

    const selectIsRefetchingPlaceTagData = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isRefetchingPlaceTagData,
    );

    const selectIsInitialPlaceTagDataLoadingDone = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isInitialPlaceTagDataLoadingDone,
    );

    const selectIsCreatingTag = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isCreatingTag,
    );

    const selectIsUpdatingTag = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isUpdatingTag,
    );

    const selectIsError = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isError,
    );

    const selectAllVenues = createSelector(
        selectMyZoneState,
        (state: MyZoneState): PurchasedPoiRowDataType[] => state.venues.map(normalizePoiData),
    );

    const selectAllChains = createSelector(
        selectMyZoneState,
        (state: MyZoneState): PurchasedPoiRowDataType[] => state.chains.map(normalizePoiData),
    );

    const selectAllBillboards = createSelector(
        selectMyZoneState,
        (state: MyZoneState): PurchasedPoiRowDataType[] => state.billboards.map(normalizePoiData),
    );

    const selectPlacesForTags = createSelector(
        selectMyZoneState,
        (state: MyZoneState): TagsRowDataType[] => {
            const estimatedFootTrafficSum: number = state.placesForTag
                ? sumBy(
                      Object.values(state.placesForTag),
                      (item: PlacerEntityWrapper<Complex>) =>
                          item.overview?.estimated_foottraffic ?? 0,
                  )
                : 0;

            return state.placesForTag
                ? Object.entries(state.placesForTag).map(([id, currentPlace], index) =>
                      normalizePlacesForTagsData({
                          currentPlace: currentPlace,
                          comparedPlace: state.comparedPlacesForTag[id],
                          estimatedFootTrafficSum,
                          dates: state.tagsDates,
                          index,
                      }),
                  )
                : [];
        },
    );

    const selectMyTagsComparisonDates = createSelector(
        selectMyZoneState,
        (state: MyZoneState): ComparisonDates | undefined => {
            return state.tagsDates;
        },
    );

    const selectPortfolioComparisonDates = createSelector(
        selectMyZoneState,
        (state: MyZoneState): ComparisonDates | undefined => {
            return state.portfolioDates;
        },
    );

    const selectPlacesForTagWithoutComparisonData = createSelector(
        selectMyZoneState,
        (state: MyZoneState): VenueSuggestion[] => {
            return state.placesForTag
                ? Object.values(state.placesForTag).map(normalizeVenueSuggestion)
                : [];
        },
    );

    const selectIsPlacesForTagLoading = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isPlacesForTagLoading,
    );

    const selectIsPlacesForTagError = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isPlacesForTagError,
    );

    const selectVenueSuggestions = createSelector(
        selectMyZoneState,
        (state: MyZoneState): VenueSuggestion[] => {
            return state.venueSuggestions
                ? state.venueSuggestions.map(normalizeVenueSuggestion)
                : [];
        },
    );

    const selectIsVenueSuggestionsError = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isVenueSuggestionsError,
    );

    const selectIsVenueSuggestionsLoading = createSelector(
        selectMyZoneState,
        (state: MyZoneState): boolean => state.isVenueSuggestionsLoading,
    );

    const selectAllCreatedPOIs = createSelector(
        selectMyZoneState,
        (state: MyZoneState): CreatedPoisRowDataType[] => state.allPois.map(normalizeCustomPoiData),
    );

    const selectGroups = createSelector(
        selectMyZoneState,
        (state: MyZoneState): PropertiesGroup[] => {
            return state.groups;
        },
    );

    const selectAllRecentEntities = createSelector(
        userCustomSettingsRecentPoiIdsSelector,
        (_: any, { reportTypes }: AllEntitiesSelectorType) => reportTypes,
        (recentEntities, reportTypes) => {
            const entityTypes: PlaceType[] = [];
            for (let x = 0; x < reportTypes.length; x++) {
                entityTypes.push(...getEntityTypesByReportType(reportTypes[x]));
            }

            const res = getRecentEntitiesByReportType(recentEntities, entityTypes);
            return sortByTimestamp(res) as RecentEntity[];
        },
    );

    const getTagsProperties = createSelector(
        selectMyZoneState,
        (state: MyZoneState) => state.tagsProperties,
    );

    const getPortfolioProperties = createSelector(
        selectMyZoneState,
        (state: MyZoneState) => state.portfolioProperties,
    );

    return {
        selectIsLoadingContent,
        selectIsError,
        selectAllVenues,
        selectAllChains,
        selectAllBillboards,
        selectPlacesForTags,
        selectMyTagsComparisonDates,
        selectPortfolioComparisonDates,
        selectIsRefetchingPlaceTagData,
        selectIsInitialPlaceTagDataLoadingDone,
        selectVenueSuggestions,
        selectIsCreatingTag,
        selectIsUpdatingTag,
        selectPlacesForTagWithoutComparisonData,
        selectIsVenueSuggestionsError,
        selectIsVenueSuggestionsLoading,
        selectIsPlacesForTagLoading,
        selectIsPlacesForTagError,
        selectAllCreatedPOIs,
        selectGroups,
        selectAllRecentEntities,
        getTagsProperties,
        getPortfolioProperties,
    };
};
